import type Store from '@ember-data/store';
import { set } from '@ember/object';
import Route from '@ember/routing/route';
import type Transition from '@ember/routing/transition';
import { service } from '@ember/service';
import ExpiredFundTokenError from 'donor-elf-web/adapters/expired-fund-token-error';
import logger from 'donor-elf-web/lib/logger';
import type AbilitiesService from 'donor-elf-web/services/abilities';
import type CurrentUser from 'donor-elf-web/services/current-user';
import type FundsService from 'donor-elf-web/services/funds-service';
import type NavbarSettingsService from 'donor-elf-web/services/navbar-settings';
import SessionService from 'donor-elf-web/services/session';
import type Settings from 'donor-elf-web/services/settings';
import RSVP from 'rsvp';

export default class AdminRoute extends Route {
  @service declare abilities: AbilitiesService;
  @service declare currentUser: CurrentUser;
  @service declare fundsService: FundsService;
  @service declare navbarSettings: NavbarSettingsService;
  @service declare session: SessionService;
  @service declare settings: Settings;
  @service declare store: Store;

  async beforeModel(transition: Transition) {
    await this.session.requireAuthentication(transition, 'login');

    if (
      this.fundsService.selectedFund == null &&
      this.currentUser.selectedFundId != null
    ) {
      const fund = await this.store.findRecord(
        'fund',
        this.currentUser.selectedFundId,
      );
      this.fundsService.selectedFund = fund;
    }
  }

  async model(_: any, transition: Transition) {
    if (!this.fundsService.selectedFund) {
      transition.abort();
      return null;
    }

    const model = await RSVP.hash({
      settings: this.settings.loadSettings(this.fundsService.selectedFund.id),
      abilities: this.abilities.loadForAllFunds(),
    });

    if (this.abilities.noAbility('view_admin_area')) {
      transition.abort();
      return null;
    }

    return model;
  }

  afterModel() {
    this.navbarSettings.topNavbarComponent = 'top-navbar-admin';
  }

  willTransition = (transition: Transition) => {
    const intentName = transition.to?.name || '';

    if (intentName.length > 0 && intentName.indexOf('admin.') !== 0) {
      this.navbarSettings.topNavbarComponent = '';
    }
  };

  error = (error: any, transition: Transition) => {
    if (error instanceof ExpiredFundTokenError) {
      logger.error(
        `invalid fund token while going to route: ${transition.to?.name}`,
      );
    }

    return true;
  };

  didTransition = () => {
    set(this.controllerFor('application'), 'currentMenuLabel', 'adminMenu');
  };
}
